import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Eye } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/status";

export function VehicleDocumentsTab({ vehicleId, editAllowed }) {
  const [docs, setDocs] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const blank = {
    document_type: "vehicle_registration",
    title: "",
    expiry_date: "",
    notes: "",
    file_url: "",
    customer_visible: false,
  };
  const [form, setForm] = useState(blank);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}/documents`);
      setDocs(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setDocs([]);
    }
  }, [vehicleId]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => {
    setForm(blank); setEditingId(null); setShowForm(true);
  };
  const openEdit = (d) => {
    setForm({
      document_type: d.document_type || "other",
      title: d.title || "",
      expiry_date: d.expiry_date || "",
      notes: d.notes || "",
      file_url: d.file_url || "",
      customer_visible: !!d.customer_visible,
    });
    setEditingId(d.id);
    setShowForm(true);
  };
  const cancel = () => { setShowForm(false); setEditingId(null); setForm(blank); };

  // Upload a file (PDF/JPG/PNG) and convert it to a base64 data URL
  // stored in `file_url`. Cap at ~5MB to keep Mongo docs small.
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^(application\/pdf|image\/(jpeg|png|webp))$/.test(file.type)) {
      toast.error("Only PDF, JPG, PNG, or WEBP files are allowed.");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large: max 5 MB.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, file_url: String(reader.result || "") }));
      toast.success(`Attached ${file.name}`);
    };
    reader.onerror = () => toast.error("Could not read file.");
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Please give the document a title."); return; }
    setSaving(true);
    try {
      const payload = {
        document_type: form.document_type,
        title: form.title.trim(),
        notes: form.notes?.trim() || null,
        expiry_date: form.expiry_date || null,
        file_url: form.file_url || null,
        customer_visible: !!form.customer_visible,
      };
      if (editingId) {
        await api.put(`/vehicles/${vehicleId}/documents/${editingId}`, payload);
        toast.success("Document updated");
      } else {
        await api.post(`/vehicles/${vehicleId}/documents`, payload);
        toast.success("Document added to this vehicle");
      }
      cancel();
      await reload();
    } catch (ex) {
      toast.error(formatApiError(ex));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/vehicles/${vehicleId}/documents/${doc.id}`);
      toast.success("Document removed");
      await reload();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  // Mark a document as "expiring soon" if expiry is within 30 days.
  const expiryTone = (iso) => {
    if (!iso) return null;
    const d = new Date(iso).getTime();
    if (isNaN(d)) return null;
    const days = Math.floor((d - Date.now()) / 86400000);
    if (days < 0) return { tone: "expired", days, cls: "bg-red-100 text-red-700 border border-red-200" };
    if (days <= 30) return { tone: "soon",    days, cls: "bg-amber-100 text-amber-800 border border-amber-200" };
    return { tone: "ok", days, cls: "bg-slate-100 text-slate-700 border border-slate-200" };
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="vd-documents">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold">Documents for this vehicle</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Registration, OR/CR, insurance, franchise/permit, emission, maintenance: only visible to your team.
          </p>
        </div>
        {editAllowed && !showForm && (
          <button
            type="button"
            onClick={openCreate}
            data-testid="vd-documents-add"
            className="px-3 py-2 rounded-lg bg-[#1B4332] text-white hover:bg-[#2D6A4F] text-sm inline-flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" />
            Add document
          </button>
        )}
      </div>

      {showForm && (
        <form
          onSubmit={submit}
          data-testid="vd-documents-form"
          className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-3"
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Document type *</span>
              <select
                data-testid="vd-doc-type"
                value={form.document_type}
                onChange={(e) => setForm({ ...form, document_type: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]"
              >
                <option value="vehicle_registration">Vehicle registration</option>
                <option value="or_cr">OR / CR (PH)</option>
                <option value="insurance">Insurance</option>
                <option value="permit_franchise">Permit / Franchise</option>
                <option value="emission_test">Emission test</option>
                <option value="maintenance_cert">Maintenance certificate</option>
                <option value="ownership_doc">Ownership document</option>
                <option value="authorization_doc">Authorization document</option>
                <option value="rental_agreement">Rental agreement</option>
                <option value="driver_license_copy">Driver license copy</option>
                <option value="receipt">Receipt</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Title *</span>
              <input
                data-testid="vd-doc-title"
                type="text"
                required
                maxLength={200}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. OR/CR: 2026"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]"
              />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Expiry date</span>
              <input
                data-testid="vd-doc-expiry"
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-700 font-medium">Attach file</span>
              <input
                data-testid="vd-doc-file"
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                onChange={handleFile}
                className="mt-1 w-full text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-slate-200 file:text-slate-700 hover:file:bg-slate-300"
              />
              {form.file_url && (
                <span className="block mt-1 text-[11px] text-emerald-700">✓ File attached ({Math.round(form.file_url.length / 1024)} KB)</span>
              )}
            </label>
          </div>

          <label className="block text-sm">
            <span className="text-slate-700 font-medium">Notes</span>
            <textarea
              data-testid="vd-doc-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Internal notes (renewal reminders, document number, etc.)"
              maxLength={2000}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none focus:border-[#2D6A4F]"
            />
          </label>

          <label className="flex items-start gap-2 text-sm rounded-lg border border-slate-200 bg-slate-50 p-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="vd-doc-customer-visible"
              checked={form.customer_visible}
              onChange={(e) => setForm({ ...form, customer_visible: e.target.checked })}
              className="mt-0.5"
            />
            <span>
              <span className="font-medium text-slate-800">Visible to the renter</span>
              <span className="block text-xs text-slate-500 mt-0.5">
                Shown on their confirmation/booking page under "View vehicle details." Off by default: only
                turn this on for documents you're comfortable sharing directly with the customer (e.g. a
                rental agreement copy), never IDs or other customers' documents.
              </span>
            </span>
          </label>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              type="submit"
              data-testid="vd-doc-save"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-[#1B4332] text-white hover:bg-[#2D6A4F] text-sm font-medium disabled:opacity-60"
            >
              {saving ? "Saving…" : editingId ? "Save changes" : "Add document"}
            </button>
            <button
              type="button"
              onClick={cancel}
              data-testid="vd-doc-cancel"
              className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm"
            >
              Cancel
            </button>
            <span className="text-[11px] text-slate-500 ml-auto">
              Internal-only by default, never shown on your public fleet page either way.
            </span>
          </div>
        </form>
      )}

      {docs === null ? (
        <div className="text-sm text-slate-500 py-3">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-sm text-slate-500 py-3" data-testid="vd-documents-empty">
          No documents yet. Add your vehicle papers (OR/CR, insurance, permits) so your team has them in one place.
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {docs.map((d) => {
            const exp = expiryTone(d.expiry_date);
            return (
              <li key={d.id} className="py-3 flex items-start justify-between gap-3" data-testid={`vd-doc-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {d.title}
                    {d.customer_visible && (
                      <span
                        data-testid={`vd-doc-${d.id}-customer-visible-badge`}
                        title="Visible to the renter on their confirmation/booking page"
                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"
                      >
                        <Eye className="w-2.5 h-2.5" /> Renter-visible
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 capitalize">{(d.document_type || "other").replace(/_/g, " ")}</div>
                  {d.notes && <p className="text-xs text-slate-600 mt-1 whitespace-pre-line line-clamp-2">{d.notes}</p>}
                  {d.file_url && (
                    <a
                      href={d.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`vd-doc-${d.id}-file`}
                      className="text-xs text-emerald-700 hover:underline mt-1 inline-block"
                    >
                      Open attached file →
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {exp && (
                    <span
                      data-testid={`vd-doc-${d.id}-expiry`}
                      className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${exp.cls}`}
                      title={`Expires ${fmtDate(d.expiry_date)}`}
                    >
                      {exp.tone === "expired"
                        ? `Expired ${Math.abs(exp.days)}d ago`
                        : exp.tone === "soon"
                        ? `Expires in ${exp.days}d`
                        : `Expires ${fmtDate(d.expiry_date)}`}
                    </span>
                  )}
                  {editAllowed && (
                    <>
                      <button
                        type="button"
                        onClick={() => openEdit(d)}
                        data-testid={`vd-doc-${d.id}-edit`}
                        className="p-1.5 rounded hover:bg-slate-100 text-slate-600"
                        title="Edit"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(d)}
                        data-testid={`vd-doc-${d.id}-delete`}
                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
